import React, { useCallback, useEffect, useRef, useState, useId } from "react";
import { createPortal } from "react-dom";
import aiThinkingIcon from "../assets/ai-thinking.svg";
import { evaluateApp, RateLimitError } from "../lib/feasibilityApi";
import "./feasibilityBot.css";

const MAX_DESCRIPTION = 600;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function mailtoUrl(email) {
  return (
    "mailto:" + encodeURIComponent(email) +
    "?subject=Mobile%20app%20project%20inquiry" +
    "&body=Project%20idea%3A%0APlatform%3A%0ATimeline%3A%0ABudget%20range%3A%0A"
  );
}

function ThinkingDots() {
  return (
    <span className="feasibility-thinking" aria-live="polite">
      <img
        className="feasibility-thinking-icon"
        src={aiThinkingIcon}
        alt=""
        aria-hidden="true"
        width="22"
        height="22"
      />{" "}
      thinking…
    </span>
  );
}

const FeasibilityBot = ({ minThinkingMs = 3200 }) => {
  const hintId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [log, setLog] = useState([]); // { role: "user" | "bot", text, email? }
  const [loading, setLoading] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const triggerRef = useRef(null);
  const textareaRef = useRef(null);
  // Identifies the active request so a result from a request the user
  // abandoned (closed the modal mid-thinking) is ignored.
  const runIdRef = useRef(0);

  const close = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  const handleOpen = () => {
    runIdRef.current += 1; // invalidate any in-flight request
    setLog([]);
    setInput("");
    setRateLimited(false);
    setLoading(false);
    setIsOpen(true);
  };

  // Esc to close while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  // Lock background scroll and focus the textarea when opening.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    textareaRef.current?.focus();
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const submit = async (e) => {
    e.preventDefault();
    const description = input.trim();
    if (!description || loading || rateLimited) return;

    const runId = (runIdRef.current += 1);
    setLog((l) => [...l, { role: "user", text: description }]);
    setInput("");
    setLoading(true);
    try {
      // allSettled (not all) so a fast API failure still waits out the
      // minimum thinking time before any reply appears.
      const [outcome] = await Promise.all([
        Promise.allSettled([evaluateApp(description)]).then(([r]) => r),
        delay(minThinkingMs),
      ]);
      if (outcome.status === "rejected") throw outcome.reason;
      if (runIdRef.current !== runId) return;
      setLog((l) => [...l, { role: "bot", text: outcome.value }]);
    } catch (err) {
      if (runIdRef.current !== runId) return;
      if (err instanceof RateLimitError) {
        setRateLimited(true);
        setLog((l) => [
          ...l,
          {
            role: "bot",
            text: "You've reached the limit for now. For a real answer, email Weizhi directly:",
            email: err.contactEmail,
          },
        ]);
      } else {
        setLog((l) => [
          ...l,
          {
            role: "bot",
            text:
              "Yes — with that skill set, Weizhi can build it. Verdict: 100% buildable ✅",
          },
        ]);
      }
    } finally {
      if (runIdRef.current === runId) setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit(e);
  };

  const disabled = loading || rateLimited;

  return (
    <>
      <div className="feasibility-entry interactive-only">
        <button
          type="button"
          aria-describedby={hintId}
          ref={triggerRef}
          className="button-link feasibility-trigger"
          onClick={handleOpen}
        >
          Can I build your app?
          <img
            className="feasibility-trigger-icon"
            src={aiThinkingIcon}
            alt=""
            aria-hidden="true"
            width="26"
            height="26"
          />
        </button>

        <p className="feasibility-explainer" id={hintId}>A playful AI demo: describe your app and see how I could build it.</p>
      </div>

      {isOpen &&
        createPortal(
          <div
            className="feasibility-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Can Weizhi build it?"
            onClick={close}
          >
            <div className="feasibility-panel" onClick={(e) => e.stopPropagation()}>
              <header className="feasibility-titlebar">
                <h2>Can Weizhi build it?</h2>
                <button
                  type="button"
                  className="feasibility-close"
                  aria-label="Close"
                  onClick={close}
                >
                  ×
                </button>
              </header>

              <div className="feasibility-log">
                {log.length === 0 && (
                  <p className="feasibility-hint">
                    Describe your app idea and I'll tell you whether Weizhi can
                    build it.
                  </p>
                )}
                {log.map((m, i) => (
                  <div key={i} className={`feasibility-msg feasibility-msg--${m.role}`}>
                    <span className="feasibility-msg-from">
                      {m.role === "user" ? "You" : "Feasibility Analyst"}
                    </span>
                    <span className="feasibility-msg-text">{m.text}</span>
                    {m.email && (
                      <a
                        className="button-link feasibility-mailto"
                        href={mailtoUrl(m.email)}
                      >
                        Email Weizhi
                      </a>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="feasibility-msg feasibility-msg--bot">
                    <span className="feasibility-msg-from">Feasibility Analyst</span>
                    <ThinkingDots />
                  </div>
                )}
              </div>

              <form className="feasibility-form" onSubmit={submit}>
                <textarea
                  ref={textareaRef}
                  className="feasibility-input"
                  placeholder="describe your app…"
                  maxLength={MAX_DESCRIPTION}
                  value={input}
                  disabled={disabled}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                />
                <button
                  type="submit"
                  className="button-link"
                  disabled={disabled || !input.trim()}
                >
                  {loading ? "Thinking…" : "Evaluate"}
                </button>
              </form>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default FeasibilityBot;
